import { useEffect, useRef, useState } from 'react';
import error from '../../assets/images/error.svg';
import existingUserIcon from '../../assets/svgs/existing-user.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { clearRegistrationState, getAccountId, getAccountInfo, getPartner } from '../../utils/storage';
import RegistrationPage from '../layouts/RegistrationPage';
import PageHeader from '../PageHeader';
import CustomButton from '../customButton/CustomButton';
import ActionLink from '../actionLink/ActionLink';
import ActionDialog from '../dialog/ActionDialog';
import { getContinuationLink } from '../../utils/getContinuationLink';
import { remindUsername } from '../../services/registration';
import { showOtpErrorToast } from '../toast/showOtpErrorToast';
import PageLoaderOverlay from '../loading/PageLoaderOverlay';
import {
    analytics,
    getLastRegistrationStep,
    resolveAnalyticsError,
    toAnalyticsPagePath,
    trackRegistrationFailed,
} from '../../analytics';

function readRemindUsernamePayload(language: string) {
    const accountInfo = getAccountInfo();
    const partner = getPartner();
    const email = String(accountInfo?.email ?? partner?.email ?? '').trim();
    const ssn = String(
        accountInfo?.ssn ?? accountInfo?.docNumber ?? partner?.docNumber ?? ''
    ).trim();

    return { email, ssn, language };
}

export default function Error() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const errorCodeParam = searchParams.get('errorCode');
    const messageParam = searchParams.get('message');
    const errorCode = errorCodeParam || 'error3012';
    const { t } = useTranslation();
    const errorInfoKey = `${errorCode}Info`;
    const title = i18n.exists(errorCode) ? t(errorCode) : t('error3012');
    const message = i18n.exists(errorInfoKey) ? t(errorInfoKey) : t('errorGeneralInfo');
    const isExistingUser = errorCode === 'error5002';
    const continuationLink = getContinuationLink();
    const trackedRef = useRef(false);
    const [remindOpen, setRemindOpen] = useState(false);
    const [isReminding, setIsReminding] = useState(false);

    useEffect(() => {
        if (trackedRef.current) {
            return;
        }

        const { error_code, error_message } = resolveAnalyticsError({
            errorCode: errorCodeParam || messageParam || '3012',
        });

        const path = toAnalyticsPagePath(
            '/error',
            searchParams.toString() ? `?${searchParams.toString()}` : ''
        );
        analytics.setCurrentPath(path);

        // Deduped fallback when a catch site did not already track terminal failure.
        // Requires application_id + stored funnel step (do not invent a step).
        const step = getLastRegistrationStep();
        if (getAccountId() && step) {
            trackRegistrationFailed({
                step,
                error_code,
                error_message,
            });
        }

        // Failure caused this screen — funnel event first, then navigation screen_view.
        analytics.pageView(path, { error_code, error_message });
        trackedRef.current = true;
    }, [errorCodeParam, messageParam, searchParams]);

    const goHome = () => {
        clearRegistrationState();
        navigate('/');
    };

    const handleRemindUsername = async () => {
        if (isReminding) {
            return;
        }

        setIsReminding(true);
        try {
            await remindUsername(readRemindUsernamePayload(i18n.language));
            setRemindOpen(true);
        } catch {
            showOtpErrorToast(t('errorGeneralInfo'));
        } finally {
            setIsReminding(false);
        }
    };

    return (
        <RegistrationPage
            contentClassName="flex flex-col items-center gap-10 md:max-w-[32.5rem] md:mx-auto"
            stickyFooter
            footer={
                isExistingUser ? (
                    <div className="mt-4 md:mt-10 flex w-full flex-col items-center gap-4">
                        <ActionLink
                            to={continuationLink}
                            className="md:mx-auto"
                            external
                            isBlank={false}
                        >
                            {t('error5002Cta')}
                        </ActionLink>
                        <CustomButton
                            type="button"
                            variant="outlined"
                            className="md:mx-auto"
                            disabled={isReminding}
                            onClick={handleRemindUsername}
                        >
                            {t('error5002RemindUsername')}
                        </CustomButton>
                    </div>
                ) : (
                    <CustomButton onClick={goHome} className="mt-4 md:mt-10 md:mx-auto">
                        {t('errorButton')}
                    </CustomButton>
                )
            }
        >
            <PageLoaderOverlay active={isReminding} />
            <PageHeader
                imgSrc={isExistingUser ? existingUserIcon : error}
                title={title}
                lead={message}
            />
            {isExistingUser ? (
                <ActionDialog
                    open={remindOpen}
                    onCancel={() => setRemindOpen(false)}
                    closeLabel={t('dialogClose')}
                    title={t('error5002RemindTitle')}
                    description={t('error5002RemindDescription')}
                    confirmButtonText={t('error5002RemindCta')}
                />
            ) : null}
        </RegistrationPage>
    );
}
